/**
 * @fileoverview
 * Performance Utilities
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function (this: any, ...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func.apply(this, args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function calls
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  
  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Memoize function results
 */
export function memoize<T extends (...args: any[]) => any>(func: T): T {
  const cache = new Map<string, ReturnType<T>>();
  
  return ((...args: Parameters<T>) => {
    const key = JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = func(...args);
    cache.set(key, result);
    return result;
  }) as T;
}

/**
 * Lazy load component
 */
export function lazyLoadComponent(
  importFn: () => Promise<any>
): Promise<any> {
  return importFn();
}

/**
 * Intersection Observer for lazy loading images
 */
export function createLazyLoadObserver(
  callback: (entry: IntersectionObserverEntry) => void,
  options?: IntersectionObserverInit
): IntersectionObserver {
  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry);
        }
      });
    },
    {
      rootMargin: '50px',
      ...options
    }
  );
}

/**
 * Request Idle Callback wrapper
 */
export function runWhenIdle(callback: () => void, timeout: number = 2000): void {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, timeout);
  }
}

/**
 * Track performance metrics
 */
export class PerformanceTracker {
  private marks: Map<string, number> = new Map();

  /**
   * Mark start of operation
   */
  mark(name: string): void {
    this.marks.set(name, performance.now());
  }

  /**
   * Measure elapsed time since mark
   */
  measure(name: string): number {
    const start = this.marks.get(name);
    if (!start) {
      console.warn(`No mark found for: ${name}`);
      return 0;
    }
    
    const duration = performance.now() - start;
    this.marks.delete(name);
    return duration;
  }

  /**
   * Log measurement
   */
  logMeasure(name: string, label?: string): void {
    const duration = this.measure(name);
    console.log(`[Performance] ${label || name}: ${duration.toFixed(2)}ms`);
  }
}

/**
 * Create performance tracker instance
 */
export const performanceTracker = new PerformanceTracker();

